import { Box, CircularProgress, Grid, styled } from '@mui/material';
import { theme } from '@side/pantry';
import { useContext, useRef, useState } from 'react';
import { PhotoControls } from './PhotoControls';
import { UploadImageModalContext } from './UploadImageModalContext';

type ImagePreviewProps = {
  uploading?: boolean;
  selectedFile: File | null;
  handleRemoveImage: () => void;
};

const StyledImage = styled('img')({
  height: '100%',
  width: '100%',
  objectFit: 'cover',
});

const StyledImageGrayscale = styled('img')<{ uploading?: boolean }>(
  ({ uploading }) => ({
    height: '100%',
    width: '100%',
    objectFit: 'cover',
    filter: uploading ? 'grayscale(100%)' : 'none',
    opacity: uploading ? 0.5 : 1,
  }),
);

const SpinnerOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PhotoContainer = styled(Box)`
  height: 168px;
  width: 168px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
  border-radius: 50%;
`;

export const ImagePreview = ({
  uploading,
  selectedFile,
  handleRemoveImage,
}: ImagePreviewProps) => {
  const outsideClickRef = useRef<HTMLElement>(null);
  const [selected, setSelected] = useState(false);

  const [state] = useContext(UploadImageModalContext);
  const { imageUrl, base64CroppedImage, base64Image } = state;

  return (
    <Grid
      container
      spacing={1}
      data-testid="photo-grid"
      sx={{
        pt: 1,
      }}
    >
      <Grid>
        {(imageUrl || selectedFile || state.base64Image) && (
          <PhotoContainer
            ref={outsideClickRef}
            onMouseEnter={() => setSelected(true)}
            onMouseLeave={() => setSelected(false)}
          >
            {!selectedFile && !base64CroppedImage ? (
              <StyledImage
                src={imageUrl ?? undefined}
                alt="uploaded from user"
              />
            ) : null}
            {base64Image || base64CroppedImage ? (
              <StyledImageGrayscale
                src={base64CroppedImage || base64Image || ''}
                alt="uploaded from user"
                uploading={uploading}
              />
            ) : null}
            {uploading && (
              <SpinnerOverlay>
                <CircularProgress sx={{ color: theme.palette.divider }} />
              </SpinnerOverlay>
            )}
            <PhotoControls
              selected={selected}
              handleDelete={handleRemoveImage}
            />
          </PhotoContainer>
        )}
      </Grid>
    </Grid>
  );
};
